import { useAppDispatch } from '@/hooks'
import { fetchAuthenticated } from '@/utils/fetch'
import { invalidateCollections } from '@/app/pages/multi-program-enrollment/utils/utils'
import { Result, ok, err } from 'neverthrow'
import {
  Thermostat,
  UtilityProgramEnrollment,
  Vehicle,
  VehicleCharger,
} from '@/types'
import { DeviceIdSelections } from '@/components/EnrollableDeviceChecklist/EnrollableDeviceChecklist'
import { EnrollmentOverview } from '@/types/programs-new/programs-new'
import { logEvent } from '@/logging'
import { ID } from '@/types/model'

type ActionSuccess = UtilityProgramEnrollment
type ActionError = {
  message: string
  status?: number
}

export type ActionParams = {
  utilityProgramId: number
  vehicleIds: number[]
  thermostatIds: number[]
  chargerIds: number[]
  skipInvalidation?: boolean
}

export function useEnrollmentActions() {
  const dispatch = useAppDispatch()

  /**
   * Performs an API call to enroll devices in a utility program
   * @param utilityProgramId - The ID of the utility program to enroll in
   * @param vehicleIds - Object containing arrays of vehicle IDs to enroll
   * @param thermostatIds - Object containing arrays of thermostat IDs to enroll
   * @param chargerIds - Object containing arrays of charger IDs to enroll
   * @returns A Result type containing either the successful data or an error
   */
  const enrollDevices = async ({
    utilityProgramId,
    vehicleIds,
    thermostatIds,
    chargerIds,
    skipInvalidation,
  }: ActionParams): Promise<Result<ActionSuccess, ActionError>> => {
    try {
      const response = await fetchAuthenticated('/programs/enroll', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          utility_program_id: utilityProgramId,
          vehicle_ids: vehicleIds,
          thermostat_ids: thermostatIds,
          charger_ids: chargerIds,
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        return err({
          message: errorText || 'Failed to enroll devices',
          status: response.status,
        })
      }

      // TODO: move this to middleware
      if (!skipInvalidation) {
        invalidateCollections(dispatch)
      }

      const data = await response.json()
      return ok(data)
    } catch (error) {
      return err({
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  /**
   * Performs an API call to unenroll devices from a utility program
   * @param utilityProgramId - The ID of the utility program to unenroll from
   * @param vehicleIds - Object containing arrays of vehicle IDs to unenroll
   * @param thermostatIds - Object containing arrays of thermostat IDs to unenroll
   * @param chargerIds - Object containing arrays of charger IDs to unenroll
   * @returns A Result type containing either the successful data or an error
   */
  const unenrollDevices = async ({
    utilityProgramId,
    vehicleIds,
    thermostatIds,
    chargerIds,
  }: ActionParams): Promise<Result<ActionSuccess, ActionError>> => {
    try {
      const response = await fetchAuthenticated('/programs/unenroll', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          utility_program_id: utilityProgramId,
          vehicle_ids: vehicleIds,
          thermostat_ids: thermostatIds,
          charger_ids: chargerIds,
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        return err({
          message: errorText || 'Failed to unenroll devices',
          status: response.status,
        })
      }

      // TODO: move this to middleware
      invalidateCollections(dispatch)

      const data = await response.json()
      return ok(data)
    } catch (error) {
      return err({
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  const logRequestProgramEnrollment = (options: {
    vehicles?: Vehicle[]
    chargers?: VehicleCharger[]
    thermostats?: Thermostat[]
    selections: DeviceIdSelections
    utilityProgramId: ID
    existingEnrollment?: EnrollmentOverview
  }) => {
    if (options.vehicles) {
      options.vehicles
        .filter((v) => options.selections.vehicleIds.includes(v.id))
        .filter((v) => {
          if (!options.existingEnrollment) {
            return true
          }

          return !options.existingEnrollment.vehicle_enrollments
            .map((v) => v.vehicle_id)
            .includes(v.id)
        })
        .forEach((v) => {
          logEvent(['request_program_enrollment'], {
            custom_event_data: {
              utility_program_id: options.utilityProgramId,
              devices_enrolled: [
                {
                  vehicle_id: v.id,
                  vehicle_model:
                    v.car.car_model?.friendly_name ?? 'Unknown Vehicle',
                  type: 'vehicle',
                },
              ],
            },
          })
        })
    }

    if (options.thermostats) {
      options.thermostats
        .filter((t) => options.selections.thermostatIds.includes(t.id))
        .filter((t) => {
          if (!options.existingEnrollment) {
            return true
          }

          return !options.existingEnrollment.thermostat_enrollments
            .map((t) => t.thermostat_id)
            .includes(t.id)
        })
        .forEach((t) => {
          logEvent(['request_program_enrollment'], {
            custom_event_data: {
              utility_program_id: options.utilityProgramId,
              devices_enrolled: [
                {
                  thermostat_id: t.id,
                  thermostat_model: t.name ?? 'Unknown Thermostat',
                  type: 'thermostat',
                },
              ],
            },
          })
        })
    }

    if (options.chargers) {
      options.chargers
        .filter((c) => options.selections.chargerIds.includes(c.id))
        .filter((c) => {
          if (!options.existingEnrollment) {
            return true
          }

          return !options.existingEnrollment.charger_enrollments
            .map((t) => t.charger_id)
            .includes(c.id)
        })
        .forEach((c) => {
          logEvent(['request_program_enrollment'], {
            custom_event_data: {
              utility_program_id: options.utilityProgramId,
              devices_enrolled: [
                {
                  charger_id: c.id,
                  charger_model: c.name ?? 'Unknown Charger',
                  type: 'charger',
                },
              ],
            },
          })
        })
    }
  }

  return {
    enrollDevices,
    unenrollDevices,
    logRequestProgramEnrollment,
  }
}

export default useEnrollmentActions
